import { clientOnlyRef } from '~/utils/clientOnlyRef';

export type BooleanAttributeOptionsMap = Record<string, Record<string, string>>;
export type HighlightAttributeEntry = {
  attribute_code: string;
  icon_code?: string;
  position?: number;
};
export type HighlightAttributeSetMap = Record<
  string,
  {
    seo_headline_visible: boolean;
    attributes: HighlightAttributeEntry[];
  }
>;

const booleanAttributeOptionsByLocale = new Map<
  string,
  BooleanAttributeOptionsMap
>();
const highlightAttributesByAttributeSetIdByLocale = new Map<
  string,
  HighlightAttributeSetMap
>();
/** Client-only invalidation bus — SSR must not subscribe to a process-global Dep. */
const booleanAttributeMetadataVersion = clientOnlyRef(0);

export function mergeBooleanAttributeOptionsForLocale(
  locale: string,
  incoming: BooleanAttributeOptionsMap
): void {
  if (!locale || typeof incoming !== 'object' || incoming == null) {
    return;
  }

  const normalizedLocale = locale.toLowerCase();
  const current = booleanAttributeOptionsByLocale.get(normalizedLocale) ?? {};

  let hasChanges = false;
  for (const [attributeCode, optionsByValue] of Object.entries(incoming)) {
    if (!attributeCode || !optionsByValue) {
      continue;
    }
    const existingOptions = current[attributeCode] ?? {};
    const mergedOptions = {
      ...existingOptions,
      ...optionsByValue
    };
    current[attributeCode] = mergedOptions;

    if (JSON.stringify(existingOptions) !== JSON.stringify(mergedOptions)) {
      hasChanges = true;
    }
  }

  booleanAttributeOptionsByLocale.set(normalizedLocale, current);
  if (hasChanges) {
    booleanAttributeMetadataVersion.value += 1;
  }
}

export function mergeHighlightAttributesForLocale(
  locale: string,
  incoming: HighlightAttributeSetMap
): void {
  if (!locale || typeof incoming !== 'object' || incoming == null) {
    return;
  }

  const normalizedLocale = locale.toLowerCase();
  const current =
    highlightAttributesByAttributeSetIdByLocale.get(normalizedLocale) ?? {};

  let hasChanges = false;
  for (const [attributeSetId, metadata] of Object.entries(incoming)) {
    if (!attributeSetId || !metadata) {
      continue;
    }

    const normalizedMetadata = {
      seo_headline_visible: Boolean(metadata.seo_headline_visible),
      attributes: Array.isArray(metadata.attributes)
        ? metadata.attributes
            .filter((entry) => entry?.attribute_code)
            .map((entry) => ({
              attribute_code: entry.attribute_code,
              icon_code: entry.icon_code,
              position: entry.position
            }))
        : []
    };

    const existingMetadata = current[attributeSetId];
    current[attributeSetId] = normalizedMetadata;

    if (
      JSON.stringify(existingMetadata) !== JSON.stringify(normalizedMetadata)
    ) {
      hasChanges = true;
    }
  }

  highlightAttributesByAttributeSetIdByLocale.set(normalizedLocale, current);
  if (hasChanges) {
    booleanAttributeMetadataVersion.value += 1;
  }
}

export function getHighlightAttributesForAttributeSet(
  locale: string,
  attributeSetId: number | string | null | undefined
):
  | {
      seo_headline_visible: boolean;
      attributes: HighlightAttributeEntry[];
    }
  | undefined {
  if (!locale || attributeSetId == null) {
    return undefined;
  }

  const attributeSetKey = String(attributeSetId);
  const normalizedLocale = locale.toLowerCase();

  const directMatch =
    highlightAttributesByAttributeSetIdByLocale.get(normalizedLocale)?.[
      attributeSetKey
    ];
  if (directMatch) {
    return directMatch;
  }

  const languageCode = normalizedLocale.split('-')[0];
  const languageOnlyMatch =
    highlightAttributesByAttributeSetIdByLocale.get(languageCode)?.[
      attributeSetKey
    ];
  if (languageOnlyMatch) {
    return languageOnlyMatch;
  }

  for (const [
    storedLocale,
    highlightAttributesByAttributeSetId
  ] of highlightAttributesByAttributeSetIdByLocale) {
    if (!storedLocale.startsWith(`${languageCode}-`)) {
      continue;
    }
    const fallbackMatch = highlightAttributesByAttributeSetId[attributeSetKey];
    if (fallbackMatch) {
      return fallbackMatch;
    }
  }

  return undefined;
}

export function getBooleanAttributeOptionLabel(
  locale: string,
  attributeCode: string,
  value: string
): string | undefined {
  if (!locale || !attributeCode || !value) {
    return undefined;
  }

  const normalizedLocale = locale.toLowerCase();
  const directMatch =
    booleanAttributeOptionsByLocale.get(normalizedLocale)?.[attributeCode]?.[
      value
    ];
  if (directMatch) {
    return directMatch;
  }

  const languageCode = normalizedLocale.split('-')[0];
  const languageOnlyMatch =
    booleanAttributeOptionsByLocale.get(languageCode)?.[attributeCode]?.[value];
  if (languageOnlyMatch) {
    return languageOnlyMatch;
  }

  for (const [storedLocale, optionsByCode] of booleanAttributeOptionsByLocale) {
    if (!storedLocale.startsWith(`${languageCode}-`)) {
      continue;
    }
    const fallbackMatch = optionsByCode?.[attributeCode]?.[value];
    if (fallbackMatch) {
      return fallbackMatch;
    }
  }

  return undefined;
}

export function clearBooleanAttributeOptionsCache(): void {
  booleanAttributeOptionsByLocale.clear();
  highlightAttributesByAttributeSetIdByLocale.clear();
  booleanAttributeMetadataVersion.value += 1;
}

export function useBooleanAttributeMetadataVersion() {
  return booleanAttributeMetadataVersion;
}

export type BooleanAttributeMetadataCacheStats = {
  locales: number;
  attributeCodes: number;
  highlightAttributeSets: number;
};

/**
 * Sizes of the module-level caches for Prometheus instrumentation (DBP-4123).
 * These maps live for the lifetime of the Node process and are never evicted,
 * so their size is a direct signal for the suspected residual memory leak.
 */
export function getBooleanAttributeMetadataCacheStats(): BooleanAttributeMetadataCacheStats {
  let attributeCodes = 0;
  for (const optionsByAttributeCode of booleanAttributeOptionsByLocale.values()) {
    attributeCodes += Object.keys(optionsByAttributeCode).length;
  }

  let highlightAttributeSets = 0;
  for (const highlightAttributesByAttributeSetId of highlightAttributesByAttributeSetIdByLocale.values()) {
    highlightAttributeSets += Object.keys(
      highlightAttributesByAttributeSetId
    ).length;
  }

  return {
    locales: booleanAttributeOptionsByLocale.size,
    attributeCodes,
    highlightAttributeSets
  };
}
